import { useBFF } from '~/composables/useBFF/useBFF';
import { useAppLocale } from '~/composables/custom/useAppLocale';
import productGetters from '~/composables/getters/productGetters';
import cartGetters from '~/composables/getters/cartGetters';
import { useCart } from '~/composables/custom/useCart/useCart';
import type { Category, Product } from '~/types/GraphQL';
import { Attributes, Facets, type Variant } from '~/components/Product/types/product';
import { useUser } from '~/composables/custom/useUser/useUser';

interface IGetProducts {
  categories?: string[];
  products?: string[];
  skus?: string[];
  offset?: number;
  limit?: number;
  returnFacets: boolean;
  filterAndSortBy?: object;
  attributes?: Attributes[];
  withVariants?: boolean;
  withNotAvailable?: boolean;
  operator?: string;
}

interface SearchCategory {
  value: string;
  children?: SearchCategory[];
}
interface StockInFilial {
  sku: string;
  stock: number;
}

const selectedSize = ref();
const validSelectedSize = ref(true);
const productAvailabilityAndStock = shallowRef({});
const stockInFilial = ref<StockInFilial[]>();
const searchCategories = ref<SearchCategory[]>([]);
const zeroProducts = ref<boolean>(false);

export const useProduct = (productId?: string) => {
  const product = useState<any>(`product-${productId}`);
  const selectedVariant = useState<Variant>(`product-selectedVariant-${productId}`);
  const loading = ref(false);
  const event = useRequestEvent();

  const { $products, $search } = useBFF();
  const { currencyCode: currency } = useAppLocale();

  const { cart } = useCart();
  const { user } = useUser();

  const setSelectedSize = (value: string) => {
    selectedSize.value = value;
    validSelectedSize.value = true;
  };
  const resetProduct = () => {
    selectedSize.value = null;
    validSelectedSize.value = true;
  };

  const sku = computed(() => selectedVariant.value?.sku);
  const availability = computed(() => productAvailabilityAndStock.value?.[selectedVariant.value?.sku]?.availability?.availableQuantity);
  const maxQty = computed<number>(() => Math.max(availability.value ? availability.value - cartGetters.getItemQtyBySku(cart.value, sku.value) : 0));
  const minQty = computed(() => Math.min((availability.value ? availability.value - cartGetters.getItemQtyBySku(cart.value, sku.value) : 0), selectedVariant.value?.minimumAmount ?? 1));

  const breadCrumbDetails = computed(() => breadcrumbs.value?.reduce((acc: Record<string, string>, { text = '' }, i) => {
    acc[`item_category${i + 1}`] = text as string;
    return acc;
  }, {}));

  const getProductToTrack = (quantity: number) => productGetters.getTrackingProduct({ ...selectedVariant.value, name: product.value?.name, key: product.value?.key }, currency, breadCrumbDetails.value, quantity);

  const handleRedirection = async ({ statusCode, redirectUrl }: { statusCode: number; redirectUrl: string }) => {
    if ([301, 302, 404, 410].includes(statusCode) && event) {
      setResponseStatus(event, statusCode);
    }

    if ([301, 302].includes(statusCode) && statusCode && redirectUrl) {
      await navigateTo(redirectUrl, { redirectCode: statusCode });
    }
  };

  const getProductById = async (id: string, attributeFilterNames?: string[]) => {
    if (!id) {
      throw new Error('Product ID is required');
    }

    loading.value = true;
    try {
      const { data } = await $products.getProductById(id, {
        attributeFilterNames: attributeFilterNames ?? [], customerGroup: user.value?.customerGroup?.key ?? '',
      });

      if (data?.statusCode) {
        handleRedirection(data);
      }

      product.value = data ?? [];
    }
    finally {
      loading.value = false;
    }
  };

  const getAvailabilityAndPrices = async ({ products, skus }: { products?: string[]; skus?: string[] }) => {
    if (!products?.length && !skus?.length) {
      return;
    }

    try {
      loading.value = true;
      const { data } = await $products.getStockAndPrice({ products, skus });

      if (productAvailabilityAndStock.value && data) {
        productAvailabilityAndStock.value = {
          ...productAvailabilityAndStock.value,
          ...data,
        };
      }
      else {
        productAvailabilityAndStock.value = data;
      }
    }
    finally {
      loading.value = false;
    }
  };

  const getAvailabilityInStore = async ({ storeId, skus }: { storeId?: string; skus: string[] }) => {
    stockInFilial.value = undefined;
    if (!storeId) {
      return;
    }

    try {
      loading.value = true;
      const { data } = await $products.getStockInStore(storeId, skus);
      stockInFilial.value = data;
    }
    finally {
      loading.value = false;
    }
  };

  const getSearchResults = async (searchQuery = '', page = 1, { filterAndSortBy, attributes = [Attributes.COLOR_CODE, Attributes.DETAIL_NAME, Attributes.COLOR, Attributes.CAPACITY_QUANTITY, Attributes.CAPACITY_UOM, Attributes.SIZE, Attributes.SIZE_ORDER, Attributes.DISCOUNT_SALE_VALUE], returnFacets = false,
  }: IGetProducts): Promise<any> => {
    try {
      loading.value = true;

      const { data } = await $search.getSearchResults(searchQuery,
        page, {
          attributeFilterNames: attributes,
          filterAndSortBy,
          facetsFilters: returnFacets ? Object.values(Facets) : [],
          returnBrand: true,
        });

      if (data?.facets && data.facets?.length && data.facets[0]?.name === 'category_path') {
        searchCategories.value = data.facets[0].values;
      }

      return data;
    }
    catch (err) {
      throw new Error(err);
    }
    finally {
      loading.value = false;
    }
  };

  const getProducts = async ({
    categories, offset = 0, limit, returnFacets = false, filterAndSortBy, products, skus, withNotAvailable, operator, attributes = [Attributes.COLOR_CODE, Attributes.DETAIL_NAME, Attributes.COLOR, Attributes.CAPACITY_QUANTITY, Attributes.CAPACITY_UOM, Attributes.SIZE, Attributes.SIZE_ORDER, Attributes.DISCOUNT_SALE_VALUE], withVariants,
  }: IGetProducts): Promise<any> => {
    try {
      loading.value = true;
      if (!categories?.length && !products?.length && !skus?.length) {
        zeroProducts.value = true;
        return;
      }
      const { data } = await $products.getProducts({
        categories,
        attributeFilterNames: attributes,
        offset,
        limit,
        filterAndSortBy,
        returnBrand: true,
        facetsFilters: returnFacets ? Object.values(Facets) : [],
        products,
        skus,
        withNotAvailable,
        operator,
        withVariants,
        customerGroup: user.value?.customerGroup?.key,
      });

      return data;
    }
    finally {
      loading.value = false;
    }
  };

  const getBreadcrumb = (productBreadcrumb: unknown) => {
    const category = (productBreadcrumb as Product & { categories: Category[] })?.categories?.find(cat => cat.ancestors?.some(ancestor => ancestor?.key?.startsWith('ShopNavigation')));
    if (!category) {
      return [];
    }
    return [...category.ancestors, { name: category.name, slug: category.slug }].slice(1).map((c, i, items) => ({
      index: i,
      text: c.name,
      link: i === 0 ? `/c/${c.slug}` : `/c/${items[0].slug}/${c.slug}`,
    }));
  };

  const breadcrumbs = computed(() => getBreadcrumb(product.value));

  const getCurentCategoryId = (productBreadcrumb: unknown) => {
    const category = (productBreadcrumb as Product & { categories: Category[] })?.categories?.find(cat => cat.ancestors?.some(ancestor => ancestor?.key?.startsWith('ShopNavigation')));
    if (!category) {
      return [];
    }
    return category.id;
  };

  const currentCategory = computed(() => getCurentCategoryId(product.value));

  const getProductsReduced = async ({ products }: { products: string[] }) => {
    if (!products.length) {
      return undefined;
    }
    try {
      loading.value = true;
      const { data } = await $products.getProductsReduced(products);

      return data;
    }
    finally {
      loading.value = false;
    }
  };

  return {
    sku,
    availability,
    maxQty,
    minQty,
    selectedColor: computed(() => selectedVariant.value?.attributes.color),
    qty: computed(() => minQty.value),
    selectedVariant: computed(() => selectedVariant.value),
    setSelectedVariant: (variant: any) => { selectedVariant.value = variant; },
    selectedSize: computed(() => selectedSize.value),
    setSelectedSize,
    validSelectedSize: computed(() => validSelectedSize.value),
    setValidSelectedSize: (value: boolean) => { validSelectedSize.value = value; },
    resetProduct,
    getProductToTrack,

    loading: computed(() => loading.value),
    product: computed(() => product.value),
    productAvailabilityAndStock: computed(() => productAvailabilityAndStock.value),
    stockInFilial: computed(() => stockInFilial.value),
    getProductById,
    getProducts,
    getProductsReduced,
    getStockAndPrice: getAvailabilityAndPrices,
    getBreadcrumb,
    getStockInStore: getAvailabilityInStore,
    getSearchResults,
    breadcrumbs,
    currentCategory,
    searchCategories: computed(() => searchCategories.value),
    zeroProducts: computed(() => zeroProducts.value),
  };
};
